import React, { createContext, type ReactNode, useContext } from 'react';

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	type AssistanceService,
	type AssistanceServiceConfig,
	type ConversationChannel,
	type Dialogues,
	type FetchStreamReturn,
	isHumanMessage,
	type RawChat,
	type SendMessageBrowserContext,
	type SendMessageDashboardContext,
	type SendMessageEditorContext,
	type SendMessageWhiteboardContext,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type BulkMessageActionPayload,
	type BulkMessageActionResponse,
	type MessageActionPayload,
	type MessageActionResponse,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { AssistanceServiceBase } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import { toStreamResponse } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/data';

import {
	isWidgetConfigV2Response,
	transformWidgetConfigV2ToLegacy,
} from '../../common/utils/csm-widget-config';
import type { WidgetResponse } from '../../common/utils/csm-widget-config/types';
import type { AuthProvider } from '../../types';

import {
	type AssistanceServiceCsmType,
	type ConversationFeedbackPayload,
	type CsmChat,
	type CsmChatMessage,
	type CsmMediaCredentials,
	type GetConversationChannelResponse,
	type MessageFeedbackPayload,
	type WidgetConfig,
} from './types';

const SESSION_ID_HEADER = 'x-csm-session-id';
const CHANNEL_ID_HEADER = 'X-Channel-Id';
const RECAPTCHA_TOKEN_HEADER = 'g-recaptcha-token';

type FetchOptions = {
	path: string;
	options: RequestInit;
};

export type RecaptchaTokenProvider = (action: string) => Promise<string | undefined>;

// Mirrors convo-ai-platform's `InteractionChannel` enum used by
// `POST /csm/v2/chat/channel/{id}/message/stream?interactionChannel=...`.
// See: https://bitbucket.org/atlassian/conversational-ai-platform/pull-requests/29329
export const InteractionChannel = {
	EMBED: 'EMBED',
	SUPPORT_SITE: 'SUPPORT_SITE',
} as const;
export type InteractionChannel = (typeof InteractionChannel)[keyof typeof InteractionChannel];

type AssistanceServiceCsmConfig = AssistanceServiceConfig & {
	/**
	 * Client-side Session ID to be sent on all requests to the assistance service.
	 * A Session ID allows anonymous requests to continue sending messages in a conversation.
	 * A Session ID also allows the server to group conversations together for all conversation types regardless of auth status.
	 * If not provided, a new session ID will be generated by the client for the lifetime of the client.
	 */
	sessionId?: string | undefined;
	/**
	 * Authentication provider used to determine the required authentication headers to be sent on the requests
	 */
	authProvider?: AuthProvider;
	/**
	 * Provider function that returns a reCAPTCHA token for anonymous user verification.
	 * Called automatically before each stream request.
	 */
	recaptchaTokenProvider?: RecaptchaTokenProvider;
	/**
	 * The intake channel attribution for conversations created via this service instance.
	 * Forwarded as the `interactionChannel` query parameter on
	 * `POST /csm/v2/chat/channel/{id}/message/stream` so the BE can attribute which
	 * surface initiated the conversation. When omitted, the BE records no intake
	 * channel for this conversation.
	 *
	 * See {@link InteractionChannel} for accepted values.
	 */
	interactionChannel?: InteractionChannel;
};

const RECAPTCHA_ACTION_SEND_MESSAGE = 'csm_widget_message';

export class AssistanceServiceCsm
	extends AssistanceServiceBase
	implements AssistanceServiceCsmType
{
	apiBasePath: string;
	private sessionId: string;
	private authProvider?: AuthProvider;
	private recaptchaTokenProvider?: RecaptchaTokenProvider;
	private interactionChannel?: InteractionChannel;

	constructor(props: AssistanceServiceCsmConfig) {
		super(props);

		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		this.sessionId = props.sessionId || uuid();

		if (!this.sessionId) {
			throw new Error('Invalid session ID');
		}

		this.apiBasePath = '/csm/v2';
		this.authProvider = props.authProvider;
		this.recaptchaTokenProvider = props.recaptchaTokenProvider;
		this.interactionChannel = props.interactionChannel;
	}

	public sendMessageStream = async ({
		agentNamedId,
		agentId,
		message,
		conversationId,
		controller,
		storeMessage = false,
		citationsEnabled = true,
		editorContext,
		whiteboardContext,
		browserContext,
		additionalContext,
		dashboardContext,
		fileIds,
	}: {
		conversationId: string;
		message: string | DocNode;
		agentNamedId: string;
		agentId?: string;
		controller?: AbortController;
		storeMessage?: boolean;
		citationsEnabled?: boolean;
		editorContext?: SendMessageEditorContext;
		whiteboardContext?: SendMessageWhiteboardContext;
		browserContext?: SendMessageBrowserContext;
		additionalContext?: Record<string, unknown>;
		dashboardContext?: SendMessageDashboardContext;
		fileIds?: string[];
	}): Promise<FetchStreamReturn<StreamMessage>> => {
		const recaptchaToken = await this.recaptchaTokenProvider?.(RECAPTCHA_ACTION_SEND_MESSAGE);

		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
			[CHANNEL_ID_HEADER]: 'csm-widget',
			...(recaptchaToken && { [RECAPTCHA_TOKEN_HEADER]: recaptchaToken }),
		});
		// If browserContext received and context is undefined, assume we're unable to retrieve the context of the page in focus
		const url = browserContext ? browserContext.context?.browserUrl : window?.location?.href;
		const body = {
			content: message,
			context: {
				browser_url: url,
				...(editorContext ? { editor: editorContext } : {}),
				...(whiteboardContext ? { whiteboard: whiteboardContext } : {}),
				// If browserUrl is inaccessable, fallback to htmlBody or canvasText if they exist
				...(browserContext
					? {
							browser: {
								htmlBody: browserContext.context?.htmlBody,
								canvasText: browserContext.context?.canvasText,
							},
						}
					: {}),
				...(dashboardContext ? { dashboardContext } : {}),
				...additionalContext,
			},
			recipient_agent_named_id: agentNamedId,
			agent_id: agentId,
			mimeType: typeof message === 'string' ? 'text/markdown' : 'text/adf',
			// don't allow store message if feature flag is off
			store_message: fg('ai_mate_show_store_messages') ? storeMessage : false,
			citations_enabled: citationsEnabled,
			// Uploaded CSM media is associated with the message via `file_ids`
			// rather than embedded in `content`. Omitted when there are none.
			...(fileIds && fileIds.length > 0 ? { file_ids: fileIds } : {}),
		};
		const options: RequestInit = {
			method: 'POST',
			headers: headers,
			body: JSON.stringify(body),
			signal: controller?.signal,
		};

		const interactionChannelParam = this.interactionChannel
			? `?interactionChannel=${this.interactionChannel}`
			: '';
		return this.fetchStream<StreamMessage>({
			path: `${this.apiBasePath}/chat/channel/${conversationId}/message/stream${interactionChannelParam}`,
			options,
		});
	};

	protected async fetch({ path, options }: FetchOptions): Promise<Response> {
		if (!this.authProvider) {
			return super.fetch({ path, options });
		}
		let retryCount = 0;
		const maxRetries = 1;
		const executeRequest = async (): Promise<Response> => {
			const headers = new Headers(
				options.headers instanceof Headers
					? options.headers
					: Object.entries(options.headers || {}),
			);
			const authHeaders = await this.authProvider?.getAuthHeaders();
			authHeaders?.forEach((value, key) => {
				headers.set(key, value);
			});
			return super.fetch({ path, options: { ...options, headers } });
		};
		try {
			const response = await executeRequest();
			return response;
		} catch (error) {
			// If the error is an ApiError and the status code is 401, invalidate the auth provider and retry the request
			if (
				error instanceof ApiError &&
				error.errorAttributes.statusCode === 401 &&
				retryCount < maxRetries
			) {
				retryCount++;
				this.authProvider.invalidate();
				return executeRequest();
			}
			throw error;
		}
	}

	protected createHeaders = (headers?: Record<string, string>): Headers => {
		const superHeaders = super.createHeaders(headers);

		return new Headers({
			[SESSION_ID_HEADER]: this.sessionId,
			...Object.fromEntries(superHeaders.entries()),
		});
	};

	public getChat = async (conversationId: string): Promise<CsmChat> => {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers: headers,
		};

		const rawChat = await this.fetchJson<RawChat>({
			path: `${this.apiBasePath}/chat/channel/${conversationId}/messages`,
			options,
		});

		return rawChat.map(
			(message): CsmChatMessage =>
				// Must be converted into StreamResponse
				isHumanMessage(message) ? message : toStreamResponse(message),
		);
	};

	public createConversationChannel = async (options?: {
		name?: string;
		dialogues?: Dialogues[];
		agentId?: string;
	}): Promise<Required<ConversationChannel>> => {
		return this.fetchJson<Required<ConversationChannel>>({
			path: `${this.apiBasePath}/chat/channel`,
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'content-type': 'application/json;charset=UTF-8',
				}),
				body: JSON.stringify({
					name: options?.name ?? generateConversationName(),
					dialogues: options?.dialogues ?? undefined,
				}),
			},
		});
	};

	public getConversationChannels = async (
		csmAiHubId: string | null,
		cursor?: string,
	): Promise<ConversationChannel[]> => {
		// TODO: revisit this code with additional logic to handle pagination. [CSMAI-814](https://product-fabric.atlassian.net/browse/CSMAI-814)
		const queryParams = new URLSearchParams({
			limit: '200',
		});

		if (cursor) {
			queryParams.set('cursor', cursor);
		}

		if (fg('csm_ai_filter_widget_convo_history')) {
			queryParams.append('interactionChannel', InteractionChannel.EMBED);
			queryParams.append('interactionChannel', InteractionChannel.SUPPORT_SITE);
		}

		const path = csmAiHubId
			? `/csm/v2/chat/${csmAiHubId}/channels?${queryParams}`
			: `/csm/v2/chat/channels?${queryParams}`;

		try {
			const response = await this.fetchJson<GetConversationChannelResponse>({
				path,
				options: {
					method: 'GET',
					headers: this.createHeaders(),
				},
			});
			return Promise.resolve(response.items);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			return Promise.reject(error);
		}
	};

	public resolveConversationAction = async (
		id: string,
		payload: MessageActionPayload,
	): Promise<MessageActionResponse> => {
		return this.fetchJson<MessageActionResponse>({
			path: `${this.apiBasePath}/chat/channel/${id}/action`,
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'content-type': 'application/json;charset=UTF-8',
				}),
				body: JSON.stringify({
					...payload,
					context: {
						...payload.context,
						browser_url: window.location.href,
					},
				}),
			},
		});
	};

	public bulkResolveConversationUIActions = async (
		conversationId: string,
		messageId: string,
		payload: BulkMessageActionPayload,
	): Promise<BulkMessageActionResponse> => {
		// The bulk API supports a maximum of 50 items per request.
		// Split into batches and process them sequentially.
		const BATCH_SIZE = 50;
		const allResponses: MessageActionResponse[] = [];

		for (let batchStart = 0; batchStart < payload.items.length; batchStart += BATCH_SIZE) {
			const batchItems = payload.items.slice(batchStart, batchStart + BATCH_SIZE);
			const batchResponses = await this.fetchJson<MessageActionResponse[]>({
				path: `${this.apiBasePath}/chat/channel/${conversationId}/message/${messageId}/tools/resolution`,
				options: {
					method: 'POST',
					headers: this.createHeaders({
						'content-type': 'application/json;charset=UTF-8',
					}),
					body: JSON.stringify({
						items: batchItems.map((item) => ({
							...item,
							context: {
								...item.context,
								browser_url: window.location.href,
							},
						})),
					}),
				},
			});
			allResponses.push(...batchResponses);
		}

		return allResponses;
	};

	public getCsmMediaUploadCredentials = async (
		conversationId: string,
	): Promise<CsmMediaCredentials> =>
		this.fetchJson<CsmMediaCredentials>({
			path: `${this.apiBasePath}/chat/channel/${encodeURIComponent(conversationId)}/media/credentials/upload`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
		});

	public getCsmMediaReadCredentials = async (
		conversationId: string,
	): Promise<CsmMediaCredentials> =>
		this.fetchJson<CsmMediaCredentials>({
			path: `${this.apiBasePath}/chat/channel/${encodeURIComponent(conversationId)}/media/credentials/read`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
		});

	public speechToText = async (blob: Blob): Promise<string> => {
		const formData = new FormData();
		formData.append('audio_recording', blob);

		const response = await this.fetchJson<{
			transcription: string;
		}>({
			path: '/speech/inference',
			options: {
				method: 'POST',
				headers: this.createHeaders(),
				body: formData,
			},
		});

		return response.transcription;
	};

	public submitMessageFeedback = async (
		payload: MessageFeedbackPayload,
		controller?: AbortController,
	): Promise<void> => {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const { conversation_channel_id, message_id } = payload;
		const options = {
			method: 'PUT',
			headers: headers,
			body: JSON.stringify(payload),
			signal: controller?.signal,
		};
		await this.fetch({
			path: `${this.apiBasePath}/chat/channel/${conversation_channel_id}/message/${message_id}/feedback`,
			options,
		});
	};

	public submitConversationFeedback = async (
		payload: ConversationFeedbackPayload,
		controller?: AbortController,
	): Promise<void> => {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const { conversation_channel_id } = payload;
		const options = {
			method: 'PUT',
			headers: headers,
			body: JSON.stringify(payload),
			signal: controller?.signal,
		};
		await this.fetch({
			path: `${this.apiBasePath}/chat/channel/${conversation_channel_id}/feedback`,
			options,
		});
	};

	public endChat = async (conversationId: string, controller?: AbortController): Promise<void> => {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers: headers,
			body: JSON.stringify({}),
			signal: controller?.signal,
		};
		await this.fetch({
			path: `${this.apiBasePath}/chat/channel/${conversationId}/end`,
			options,
		});
	};

	public getWidgetConfig = async (widgetId?: string | null): Promise<WidgetConfig> => {
		const response = await this.fetchJson<WidgetResponse | WidgetConfig>({
			path: widgetId
				? `${this.apiBasePath}/widgets/${widgetId}`
				: `${this.apiBasePath}/chat/config`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
		});

		if (isWidgetConfigV2Response(response)) {
			return transformWidgetConfigV2ToLegacy(response);
		}

		return response;
	};

	public sendMessageConfirmedTools = async (
		_payload: Parameters<AssistanceService['sendMessageConfirmedTools']>[0],
	): Promise<FetchStreamReturn<StreamMessage>> => {
		// NOT implemented for CSM
		return Promise.resolve({} as FetchStreamReturn<StreamMessage>);
	};

	public getAuthHeaders = async (): Promise<Headers | null> => {
		return this.authProvider?.getAuthHeaders() ?? null;
	};

	public invalidateAuth = (): void => {
		this.authProvider?.invalidate();
	};
}

const AssistanceServiceCsmContext = createContext<AssistanceServiceCsmType | null>(null);

export const AssistanceServiceCsmProvider = ({
	value,
	children,
}: {
	value: AssistanceServiceCsmType;
	children: ReactNode;
}): React.JSX.Element => (
	<AssistanceServiceCsmContext.Provider value={value}>
		{children}
	</AssistanceServiceCsmContext.Provider>
);

/** If these props are provided and one of them are populated, they will take precedence over the `AssistanceServiceProvider` with the missing props filled in */
export const useAssistanceService = (): AssistanceServiceCsmType => {
	const service = useContext(AssistanceServiceCsmContext);

	if (!service) {
		throw new Error('`useAssistanceService` must be rendered with `AssistanceServiceCsmProvider`');
	}

	return service;
};

/**
 * Non-throwing variant for optional features that must not make the provider
 * a hard requirement of the component tree they live in. Chat input renders in
 * contexts (examples, isolated tests) without an
 * `AssistanceServiceCsmProvider`, so the attachment affordances degrade
 * instead of crashing the composer.
 */
export const useOptionalAssistanceService = (): AssistanceServiceCsmType | null =>
	useContext(AssistanceServiceCsmContext);
